/**
 * Report builder - thin typed client over /api/crm/report-builder/* and /api/crm/saved-reports*
 * (docs/plans/crm-report-builder-search-collab-plan.md B4.5; backend/src/routes/crmReportBuilder.js),
 * in the layout of recruit/api.ts. Every answer goes through builderLogic's tolerant readers, so a
 * component never meets a half-shaped object; every failure surfaces the server's plain sentence
 * (reportErrorMessage); isReportBuilderUnavailable lets the Custom tab say "not available yet" while
 * the api half is not deployed (the outreach and recruit rule: a 404 or 5xx reads as "not yet").
 *
 * What is sent: the spec exactly as plan B4.3 writes it (builderLogic toWireSpec), plus the browser's
 * IANA time zone as `tz` beside it on run and export (the server buckets days in it, and falls back
 * to America/New_York for anything that is not a zone name). Runs are aggregates and are not
 * audited; exports and saves are, by the server (plan B4.7).
 */
import api from '../../services/api';
import { triggerBlobDownload } from '../lib';
import { csvFileName, readCatalog, readResult, readSavedReport, readSavedSummary, toWireSpec } from './builderLogic';
import type {
  ReportCatalog,
  ReportResult,
  ReportSpec,
  SaveReportInput,
  SavedReport,
  SavedReportScope,
  SavedReportSummary,
  UpdateReportInput,
} from './types';

const BUILDER = '/api/crm/report-builder';
const SAVED = '/api/crm/saved-reports';
const DEFAULT_TZ = 'America/New_York';

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

/** The plain sentence to show for a failed call. Never a raw status line or stack. */
export function reportErrorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const res = (e as any)?.response;
  const data = res?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  if (res?.status === 403) return "You don't have access to that report.";
  if (res?.status === 404) return "That report wasn't found. It may have been archived or made private.";
  return fallback;
}

/** True when the builder isn't served by this api yet (an older deploy) or the api is down. */
export function isReportBuilderUnavailable(e: unknown): boolean {
  const status = (e as any)?.response?.status;
  return status === 404 || status === 502 || status === 503 || !(e as any)?.response;
}

/** The browser's IANA time zone, which the server buckets days, weeks and months in. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;
  } catch {
    return DEFAULT_TZ;
  }
}

/** A Blob's text (FileReader where Blob.text is missing, as in older browsers and jsdom). */
function blobText(blob: Blob): Promise<string> {
  const withText = blob as Blob & { text?: () => Promise<string> };
  if (typeof withText.text === 'function') return withText.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * With responseType 'blob' a refusal's JSON body arrives as a Blob; read it back in place so
 * reportErrorMessage shows the server's sentence for an export too.
 */
async function readBlobError(e: unknown): Promise<void> {
  const res = (e as any)?.response;
  if (!res || typeof Blob === 'undefined' || !(res.data instanceof Blob)) return;
  try {
    res.data = JSON.parse(await blobText(res.data));
  } catch {
    res.data = {};
  }
}

/** The file name in a Content-Disposition header (filename* first), or null. */
function fileNameFromDisposition(disposition: unknown): string | null {
  if (typeof disposition !== 'string') return null;
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star) {
    try {
      const decoded = decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
      if (decoded) return decoded;
    } catch {
      /* a malformed filename* falls through to filename= */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return plain && plain[1].trim() ? plain[1].trim() : null;
}

const reportOf = (data: unknown): SavedReport => {
  const body = data as { report?: unknown } | null | undefined;
  const report = readSavedReport(body && body.report !== undefined ? body.report : data);
  if (!report) throw new Error('The server answered without the report.');
  return report;
};

const reportPath = (id: string): string => `${SAVED}/${encodeURIComponent(id)}`;

// ---------------------------------------------------------------------------------------------
// The builder: catalog, run, export
// ---------------------------------------------------------------------------------------------

/** GET /report-builder/catalog: the caller's catalog (admin-only entries only for admins). */
export async function getCatalog(): Promise<ReportCatalog> {
  const { data } = await api.get(`${BUILDER}/catalog`);
  return readCatalog(data);
}

/** POST /report-builder/run: the result. A 400 carries the server's sentence (reportErrorMessage). */
export async function runReport(spec: ReportSpec): Promise<ReportResult> {
  const sent = toWireSpec(spec);
  const { data } = await api.post(`${BUILDER}/run`, { spec: sent, tz: browserTimeZone() });
  return readResult(data, sent);
}

/**
 * POST /report-builder/export and save the CSV (aggregates only; the server audits it). The file is
 * named after the report when `name` is given (csvFileName), else as the server names it. Answers
 * the file name used.
 */
export async function exportReport(spec: ReportSpec, reportId?: string | null, name?: string | null): Promise<string> {
  const body: { spec: ReportSpec; tz: string; report_id?: string } = { spec: toWireSpec(spec), tz: browserTimeZone() };
  if (reportId) body.report_id = reportId;
  const res = await api.post(`${BUILDER}/export`, body, { responseType: 'blob' }).catch(async (e: unknown) => {
    await readBlobError(e);
    throw e;
  });
  const fileName = name && name.trim()
    ? csvFileName(name)
    : (fileNameFromDisposition(res.headers?.['content-disposition']) ?? csvFileName(null));
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv;charset=utf-8' });
  triggerBlobDownload(blob, fileName);
  return fileName;
}

// ---------------------------------------------------------------------------------------------
// Saved reports
// ---------------------------------------------------------------------------------------------

/** GET /saved-reports?scope=mine|team: My reports or Team reports (no spec bodies). */
export async function listSavedReports(scope: SavedReportScope): Promise<SavedReportSummary[]> {
  const { data } = await api.get(SAVED, { params: { scope } });
  const list: unknown[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
  return list
    .map((row) => readSavedSummary(row))
    .filter((row): row is SavedReportSummary => row !== null);
}

/**
 * GET /saved-reports/:id: the report with its spec. `invalid` carries the server's sentence when the
 * saved spec no longer validates (it does not run). Another person's private report is a 404.
 */
export async function getSavedReport(id: string): Promise<SavedReport> {
  const { data } = await api.get(reportPath(id));
  return reportOf(data);
}

/** POST /saved-reports: save a new report (private or shared with the team). */
export async function saveReport(input: SaveReportInput): Promise<SavedReport> {
  const body: { name: string; description?: string; spec: ReportSpec; visibility: SaveReportInput['visibility'] } = {
    name: input.name.trim(),
    spec: toWireSpec(input.spec),
    visibility: input.visibility,
  };
  const description = input.description ? input.description.trim() : '';
  if (description) body.description = description;
  const { data } = await api.post(SAVED, body);
  return reportOf(data);
}

/**
 * PATCH /saved-reports/:id: change any of name, description (null or '' clears it), spec and
 * visibility. Only the author, or an admin for a team report, may (403 otherwise).
 */
export async function updateReport(id: string, patch: UpdateReportInput): Promise<SavedReport> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name.trim();
  if (patch.description !== undefined) {
    const description = patch.description ? patch.description.trim() : '';
    body.description = description || null;
  }
  if (patch.spec !== undefined) body.spec = toWireSpec(patch.spec);
  if (patch.visibility !== undefined) body.visibility = patch.visibility;
  const { data } = await api.patch(reportPath(id), body);
  return reportOf(data);
}

/** POST /saved-reports/:id/archive: it stops listing (its audit rows remain). Answers the server's ok. */
export async function archiveReport(id: string): Promise<boolean> {
  const { data } = await api.post(`${reportPath(id)}/archive`);
  return data?.ok !== false;
}

/** POST /saved-reports/:id/copy: a private copy for the caller, optionally renamed. */
export async function copyReport(id: string, name?: string | null): Promise<SavedReport> {
  const trimmed = name ? name.trim() : '';
  const { data } = await api.post(`${reportPath(id)}/copy`, trimmed ? { name: trimmed } : {});
  return reportOf(data);
}
